<template>
  <div class="delivery-page-component" :class="{ locked: isLocked }">
    <spinner-overlay v-if="loading" />
    <div v-if="hasCompletedFirstLoad" class="action-container">
      <div v-if="warnings.yellow > 0 || warnings.red > 0"
        class="d-flex justify-content-between text-right flex-column pl-2 ml-3">
        <div v-if="warnings.yellow > 0">
          <span v-for="i in warnings.yellow" :key="i" class="interval-indicator bg-warning"></span>
          <span class="interval-count">{{ warnings.yellow }}</span>
        </div>
        <div v-if="warnings.red > 0">
          <span v-for="i in warnings.red" :key="i" class="interval-indicator bg-danger"></span>
          <span class="interval-count">{{ warnings.red }}</span>
        </div>
      </div>
      <div class="ready-for-print-container">
        <label for="is-ready-for-print">
          <span class="mr-2">Status</span>
          <toggle id="is-ready-for-print" v-model="isReadyForPrint" activeColor="#74A47F" />
        </label>
      </div>
      <button type="button" class="btn btn-primary ml-3" id="show-booking-guide-btn" @click="openBookingGuideModal">
        Booking guide
      </button>
      <button v-if="!isLocked" :disabled="loading" class="btn btn-primary ml-3" @click="openModal()">
        Opret stop
      </button>
      <route-data-logs :disabled="isLocked" ref="data_logs" :delivery_zone_id="delivery_zone_id"
        :date_string="date_string" @applyLog="(log) => applyLog(log)" class="d-flex ml-3" />
      <button v-if="!isLocked" :disabled="loading" :class="{ 'btn-primary': isDirty !== false }"
        class="btn btn-secondary ml-3" @click="saveRouteData">
        Gem
      </button>
      <div v-if="isLocked" class="position-relative d-flex">
        <button class="btn btn-secondary ml-2 d-flex align-items-center"
          @click="showLockedTooltip = !showLockedTooltip">
          <i class="fas fa-lock"></i>
        </button>
        <div v-if="showLockedTooltip" class="locked-tooltip">
          <p>
            Siden er låst, da brugeren "{{ isLockedByUserName }}" er i gang med at
            redigere.
          </p>
          <p>
            Tryk på
            <button class="btn btn-danger btn-sm" @click="forceTakeLock">
              denne knap
            </button>
            for at overtage siden, og derved forkaste eventuelle ændringer den aktive
            bruger har lavet.
          </p>
        </div>
      </div>
    </div>
    <div class="table-responsive">
      <table class="table table-sm">
        <thead>
          <tr>
            <th class="text-white bg-dark" colspan="2">Status</th>
            <th class="column-delimiter"></th>
            <th class="text-white bg-dark" colspan="6">Kundeinformationer</th>
            <th class="column-delimiter"></th>
            <th class="text-white bg-dark" colspan="7">Booking</th>
            <th class="column-delimiter"></th>
            <th class="text-white bg-dark" colspan="2">Kørsel</th>
          </tr>
          <tr>
            <th>Status</th>
            <th></th>
            <th class="column-delimiter"></th>
            <th>Kuverter</th>
            <th>Produkt</th>
            <th>Kasser</th>
            <th>Levering</th>
            <th>
              Addresse
              <a :href="googleMapsDefaultUrl" class="text-primary p-2 ml-2" target="_blank"><i
                  class="fas fa-map-marker-alt"></i></a>
            </th>
            <th>Leverings interval</th>
            <th class="column-delimiter"></th>
            <th>Aktivitet</th>
            <th>Område</th>
            <th>Tur</th>
            <th>Stop</th>
            <th>Start</th>
            <th>Varighed</th>
            <th>Slut</th>
            <th class="column-delimiter"></th>
            <th>Køretøj</th>
            <th>Chauffør</th>
          </tr>
        </thead>
        <template v-for="(orders, tripName) in trips" :key="tripName">
          <template v-if="orders.length > 0">
            <tbody v-if="tripName === 'none'" key="trip-none" :class="[`trip-block-${tripName}`]">
              <template v-for="(order, index) in orders" :key="order.id">
                <delivery-row v-if="order" :order="order" :delivery_zones="delivery_zones"
                  :delivery_zone_id="delivery_zone_id" @editStop="openModal"></delivery-row>
              </template>
            </tbody>

            <tbody v-if="tripName !== 'none'" :key="tripName" :class="[`trip-block-${tripName}`]">
              <delivery-start-row ref="startRows" :trip_name="tripName" :first_order="orders[0]" :vehicles="vehicles"
                :in_use_drivers="in_use_drivers" :in_use_vehicles="in_use_vehicles" :vehicle_capacity_categories="vehicle_capacity_categories"
                :drivers="drivers" :total_quantity="tripTotalQuantity(orders)" :box_count="tripBoxCount(orders)"
                @vehicleChange="(id) => vehicleChange(tripName, id)"
                @driverChange="(id) => driverChange(tripName, id)" />
              <template v-for="(order, index) in orders" :key="order.id">
                <delivery-row v-if="order" :trip_name="tripName" :order="order" :first_order="orders[0]"
                  :delivery_zones="delivery_zones" :delivery_zone_id="delivery_zone_id" :index="index"
                  :safeTransportTimeLimits="safeTransportTimeLimits" @editStop="openModal"></delivery-row>
              </template>
              <delivery-end-row ref="endRows" :trip_name="tripName" :last_order="orders[orders.length - 1]"
                :index="orders.length" />
              <delivery-break-row ref="breakRows" :trip_name="tripName" :last_order="orders[orders.length - 1]" />
            </tbody>
          </template>
        </template>
      </table>
    </div>
  </div>
  <!-- Modal -->
  <div ref="saveStopModal" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 v-if="editingStop.id === null" class="modal-title">Opret nyt stop</h5>
          <h5 v-else class="modal-title">Rediger stop</h5>
          <button type="button" class="btn-close" @click="closeModal">X</button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Beskrivelse</label>
            <input v-model="editingStop.description" type="text" class="form-control" placeholder="F.eks. Kunde XYZ">
          </div>
          <div class="mb-3">
            <label class="form-label">Adresse</label>
            <input v-model="editingStop.address" type="text" class="form-control"
              placeholder="F.eks. Industrivej 10, 6000 Kolding">
          </div>
        </div>
        <div class="modal-footer">
          <button v-if="showDeleteStopButton" type="button" class="btn btn-danger" @click="deleteStop">Slet</button>
          <button class="btn btn-secondary" @click="closeModal">Annuller</button>
          <button class="btn btn-primary" @click="saveStop">Gem</button>
        </div>
      </div>
    </div>
  </div>
  <booking-guide-modal ref="bookingGuideModal"></booking-guide-modal>
</template>

<script>
import DeliveryBreakRow from "@/components/DeliveryBreakRow.vue";
import DeliveryEndRow from "@/components/DeliveryEndRow.vue";
import DeliveryRow from "@/components/DeliveryRow.vue";
import DeliveryStartRow from "@/components/DeliveryStartRow.vue";
import RouteDataLogs from "@/components/RouteDataLogs.vue";
import SpinnerOverlay from "@/components/SpinnerOverlay.vue";
import TimePicker from "@/components/TimePicker.vue";
import emitter from "@/mitt";
import { Order } from "@/models/order";
import { useRouteDataStore } from "@/stores/route-data";
import axios from "axios";
import moment from "moment/moment";
import { computed, onMounted, reactive, ref } from "vue";
import consumer from "@/../channels/consumer";
import { random } from "@/utils/String";
import * as bootstrap from 'bootstrap'
import Toggle from '@/components/Toggle.vue';

const startRows = ref([]);
const endRows = ref([]);
const breakRows = ref([]);
const data_logs = ref(null);

export default {
  components: {
    DeliveryRow,
    DeliveryStartRow,
    DeliveryEndRow,
    DeliveryBreakRow,
    RouteDataLogs,
    TimePicker,
    SpinnerOverlay,
    Toggle,
  },
  props: {
    date_string: {
      type: String,
      required: true,
    },
    delivery_zone_id: {
      type: Number,
      required: true,
    },
    delivery_zones: {
      type: Array,
      required: true,
    },
    vehicles: {
      type: Array,
      required: true,
    },
    vehicle_capacity_categories: {
      type: Array,
      required: true,
    },
    drivers: {
      type: Array,
      required: true,
    },
    user_id: {
      type: Number,
      required: true,
    },
    settings: {
      type: Object,
      required: true,
    },
    in_use_drivers: {
      type: Array,
      required: true,
    },
    in_use_vehicles: {
      type: Array,
      required: true,
    },
    ready_for_print: {
      type: Boolean,
      required: true,
    }
  },
  setup(props, context) {
    const loading = ref(true);
    const hasCompletedFirstLoad = ref(false);
    const isLocked = ref(true);
    const isLockedByUserName = ref("");
    const showLockedTooltip = ref(false);
    const bookingGuideModal = ref(null);
    const isReadyForPrint = ref(props.ready_for_print);
    const openBookingGuideModal = () => {
      bookingGuideModal.value.open();
    };

    const rnd = random(8);
    const sessionId = props.user_id + "_" + rnd;
    const chatChannel = consumer.subscriptions.create(
      {
        channel: "DeliveryPlanningChannel",
        id: props.date_string + "_" + props.delivery_zone_id,
        user_id: props.user_id,
        random: rnd,
      },
      {
        connected() {
          console.log("connected");
          // Called when the subscription is ready for use on the server
          // ping every 6 hours to keep the cache alive
          const sixHoursInMilliseconds = 6 * 60 * 60 * 1000;
          setInterval(() => {
            chatChannel.send({ type: "ping" });
          }, sixHoursInMilliseconds);
        },
        disconnected() {
          console.log("disconnected");
          // Called when the subscription has been terminated by the server
          isLocked.value = true;
        },
        received(data) {
          console.log("received", data);
          if (data.type === "queue update") {
            // if this session is not in the queue, reload the page
            if (!data.queue || !data.queue.includes(sessionId)) {
              // this should only happen if another user has forced the lock
              isLocked.value = true;
              location.reload();
            } else {
              isLocked.value = data.queue[0] !== sessionId;
              isLockedByUserName.value = data.user_name_with_lock;
              if (!isLockedByUserName.value) {
                console.error("isLockedByUserName", data);
              }
            }
          } else if (data.type === "data saved") {
            // if the data is saved by another user, reload the data
            if (data.sender !== sessionId) {
              loadData();
              data_logs.value.loadLogs();
            }
          }
        },
      }
    );

    const forceTakeLock = () => {
      chatChannel.send({ type: "force take lock", sender: sessionId });
      showLockedTooltip.value = false;
    };

    const routeDataStore = useRouteDataStore();
    const date = computed(() => {
      return moment(props.date_string);
    });
    const googleMapsDefaultUrl = `https://www.google.com/maps/dir/?api=1&origin="${encodeURI(
      "Industrigrenen 15, 2635 Ishøj"
    )}`;
    const orders = ref([]);
    const ordersWithoutManualStops = computed(() => {
      return orders.value.filter((order) => !order.isManualStop);
    });
    const onlyManualStops = computed(() => {
      return orders.value.filter((order) => order.isManualStop);
    });
    const loadData = () => {
      axios
        .get(
          `/order_management/orders/orders_data?type=deliveries&date=${props.date_string}&delivery_zone=${props.delivery_zone_id}`
        )
        .then((response) => {
          const allOrders = response.data.orders;
          if (response.data.ready_for_print !== undefined) {
            isReadyForPrint.value = response.data.ready_for_print;
          }
          const manualStops = response.data.route_data ? response.data.route_data.manual_stops ?? [] : [];
          for (let i = 0; i < manualStops.length; i++) {
            const stop = manualStops[i];
            allOrders.push({
              id: stop.id,
              isManualStop: true,
              delivery_zone_id: stop.delivery_zone_id,
              route_code: stop.route_code,
              tour_code: stop.tour_code,
              expected_delivery_time: stop.expected_delivery_time,
              manualStopAddress: stop.manualStopAddress,
              manualStopDescription: stop.manualStopDescription,
              vehicle_id: stop.vehicle_id,
              driver_id: stop.driver_id,
            });
          }

          orders.value = allOrders.map((order) => {
            return Order.createReactive(order);
          });

          let newRouteData = null;
          if (response.data.route_data) {
            // console.log('A');
            newRouteData = response.data.route_data;
          } else {
            // console.log('B');
            newRouteData = populateRouteData(response.data.orders);
          }

          console.log("newRouteData", newRouteData);

          newRouteData = setAllVariables(orders.value, newRouteData);
          routeDataStore.setRouteData(newRouteData);
          // console.log('routeDataStore', newRouteData);

          // set drive start for orders that have no drive start and the previous order has a delivery end
          const keys = Object.keys(trips.value);
          for (let i = 0; i < keys.length; i++) {
            const trip = keys[i];
            const tripOrders = trips.value[trip];
            for (let o = 1; o < tripOrders.length; o++) {
              const order = tripOrders[o];
              const prevOrder = tripOrders[o - 1];
              if (prevOrder.deliveryEnd && !order.driveStart) {
                order.driveStart = prevOrder.deliveryEnd;
              }
            }
          }
        })
        .finally(() => {
          loading.value = false;
          hasCompletedFirstLoad.value = true;
        });
    };
    loadData();
    const setAllVariables = (orders, routeData) => {
      // ensure all variables are defined
      for (let i = 0; i < orders.length; i++) {
        const orderId = orders[i].id;
        if (!Object.hasOwn(routeData.drive_durations, orderId)) {
          routeData.drive_durations[orderId] = null;
        }
        if (!Object.hasOwn(routeData.service_durations, orderId)) {
          routeData.service_durations[orderId] = 10;
        }
      }
      for (let i = 0; i < tripNames.length; i++) {
        if (!Object.hasOwn(routeData.service_durations, `${tripNames[i]}_start`)) {
          routeData.service_durations[`${tripNames[i]}_start`] = 10;
        }
        if (!Object.hasOwn(routeData.service_durations, `${tripNames[i]}_end`)) {
          routeData.service_durations[`${tripNames[i]}_end`] = 10;
        }
        if (!Object.hasOwn(routeData.service_durations, `${tripNames[i]}_break`)) {
          routeData.service_durations[`${tripNames[i]}_break`] = 0;
        }
        if (!Object.hasOwn(routeData.drive_durations, `${tripNames[i]}_end`)) {
          routeData.drive_durations[`${tripNames[i]}_end`] = null;
        }
      }
      return routeData;
    };
    const populateRouteData = () => {
      const drive_durations = {};
      const service_durations = {};
      const keys = Object.keys(trips.value);
      for (let i = 0; i < keys.length; i++) {
        let lastStop = 0;
        const trip = keys[i];
        const tripOrders = trips.value[trip];
        if (tripOrders.length == 0) {
          continue;
        }
        service_durations[trip + "_start"] = 10;
        for (let j = 0; j < tripOrders.length; j++) {
          const order = tripOrders[j];
          const from = lastStop;
          const to = order.id;
          if (j > 0) {
            const orderTime = order.expected_delivery_time
              ? moment(order.expected_delivery_time.replace(/Z.*/, ""))
              : null;
            const prevOrder = tripOrders[j - 1];
            const prevOrderTime = prevOrder.expected_delivery_time
              ? moment(prevOrder.expected_delivery_time.replace(/Z.*/, "")).add(
                10,
                "minutes"
              )
              : null;
            const duration =
              orderTime && prevOrderTime
                ? orderTime.diff(prevOrderTime, "minutes")
                : null;
            drive_durations[to] = duration;
          } else {
            drive_durations[to] = null;
          }
          service_durations[to] = 10;
          lastStop = to;
        }
        service_durations[trip + "_end"] = 5;
        // drive_durations[tripOrders[0].id] = 5;
        // drive_durations[trip + '_end'] = 15;
      }
      // service_durations['A_break'] = 40;
      // console.log({ drive_durations, service_durations });
      return { drive_durations, service_durations };
    };
    const tripNames = ["A", "B", "C", "D", "E", "F", "G"];
    const trips = computed(() => {
      const ordersByTrips = {
        none: [],
        A: [],
        B: [],
        C: [],
        D: [],
        E: [],
        F: [],
        G: [],
      };
      for (let i = 0; i < orders.value.length; i++) {
        const order = orders.value[i];
        if (order.route_code && order.delivery_zone_id === props.delivery_zone_id) {
          ordersByTrips[order.route_code].push(order);
        } else {
          ordersByTrips.none.push(order);
        }
      }
      for (let i = 0; i < tripNames.length; i++) {
        ordersByTrips[tripNames[i]].sort((a, b) => {
          return a.tour_code - b.tour_code;
        });
      }
      ordersByTrips.none.sort((a, b) => {
        if (a.isManualStop && b.isManualStop) {
          return a.id.localeCompare(b.id);
        } else if (a.isManualStop) {
          return -1;
        } else if (b.isManualStop) {
          return 1;
        } else if (a.deliveryIntervalStart === b.deliveryIntervalStart) {
          return a.id - b.id;
        } else {
          return a.deliveryIntervalStart.localeCompare(b.deliveryIntervalStart);
        }
      });
      return ordersByTrips;
    });
    const warnings = computed(() => {
      // loop orders
      const res = {
        green: 0,
        yellow: 0,
        red: 0,
      };
      for (let i = 0; i < ordersWithoutManualStops.value.length; i++) {
        const order = ordersWithoutManualStops.value[i];

        Object.keys(order.errorOverviewCounts).forEach(color => {
          res[color] += order.errorOverviewCounts[color];
        });
      }


      return res;
    });

    emitter.on("order-move-up", (e) => {
      console.log("order-move-up", e);
      if (!e.trip_name) {
        return;
      }
      const trip = trips.value[e.trip_name];
      const order = trip.find((o) => o.id === e.id);
      const isCurrentOrderFirst = trip[0].id === order?.id;
      const minutesAdded =
        !e.value || !e.prev_value
          ? 0
          : moment(e.value, "HH:mm").diff(moment(e.prev_value, "HH:mm"), "minutes");
      if (isCurrentOrderFirst) {
        const prevOrder = getLastOrderInTrip(getPrevTripName(e.trip_name));
        if (prevOrder && prevOrder.tripEnd) {
          const originalStart = moment(order.tripStart, "HH:mm")
            .add(-minutesAdded, "minutes")
            .format("HH:mm");
          const diff = moment(prevOrder.tripEnd, "HH:mm").diff(
            moment(originalStart, "HH:mm"),
            "minutes"
          );
          // // console.log(originalStart, diff, minutesAdded, prevOrder.tripEnd, '===', originalStart, prevOrder.tripEnd, '>', order.tripStart, '=', prevOrder.tripEnd === originalStart, prevOrder.tripEnd > order.tripStart);
          if (
            prevOrder.tripEnd === originalStart ||
            prevOrder.tripEnd > order.tripStart
          ) {
            prevOrder.moveOrder(minutesAdded - diff, "up");
          }
        }
      } else {
        const prevOrder = getPrevOrder(e.id, e.trip_name);
        console.log("prevOrder", prevOrder);
        if (prevOrder) {
          if (prevOrder.deliveryEnd) {
            const originalStart = moment(order.driveStart, "HH:mm")
              .add(-minutesAdded, "minutes")
              .format("HH:mm");
            const diff = moment(prevOrder.deliveryEnd, "HH:mm").diff(
              moment(originalStart, "HH:mm"),
              "minutes"
            );
            // console.log(originalStart, diff, prevOrder.deliveryEnd === originalStart, prevOrder.deliveryEnd > order.driveStart);
            if (
              true ||
              prevOrder.deliveryEnd === originalStart ||
              prevOrder.deliveryEnd > order.driveStart
            ) {
              prevOrder.moveOrder(minutesAdded - diff, "up");
            }
          } else {
            prevOrder.deliveryEnd = order.driveStart;
          }
        }
      }
    });

    emitter.on("order-move-down", (e) => {
      console.log("order-move-down", e);
      if (!e.trip_name) {
        return;
      }
      const trip = trips.value[e.trip_name];
      const order = trip.find((o) => o.id === e.id);
      const isCurrentOrderLast = trip[trip.length - 1].id === order.id;
      const minutesAdded =
        !e.value || !e.prev_value
          ? 0
          : moment(e.value, "HH:mm").diff(moment(e.prev_value, "HH:mm"), "minutes");
      if (isCurrentOrderLast) {
        // console.log('isCurrentOrderLast', e.trip_name);
        const nextOrder = getFirstOrderInTrip(getNextTripName(e.trip_name));
        if (nextOrder && nextOrder.tripStart) {
          const originalEnd = moment(order.tripEnd, "HH:mm")
            .add(-minutesAdded, "minutes")
            .format("HH:mm");
          const diff = moment(nextOrder.tripStart, "HH:mm").diff(
            moment(originalEnd, "HH:mm"),
            "minutes"
          );
          // console.log(originalEnd, diff, nextOrder.tripStart, '===', originalEnd, nextOrder.tripStart, '<', order.tripEnd, '=', nextOrder.tripStart === originalEnd, nextOrder.tripStart < order.tripEnd);
          if (
            nextOrder.tripStart === originalEnd ||
            nextOrder.tripStart < order.tripEnd
          ) {
            nextOrder.moveOrder(minutesAdded - diff, "down");
          }
        }
      } else {
        const nextOrder = getNextOrder(e.id, e.trip_name);
        // console.log('nextOrder', nextOrder);
        if (nextOrder) {
          if (nextOrder.driveStart) {
            const originalEnd = moment(order.deliveryEnd, "HH:mm")
              .add(-minutesAdded, "minutes")
              .format("HH:mm");
            const diff = moment(nextOrder.driveStart, "HH:mm").diff(
              moment(originalEnd, "HH:mm"),
              "minutes"
            );
            console.log(
              originalEnd,
              minutesAdded,
              diff,
              nextOrder.driveStart,
              "===",
              originalEnd,
              nextOrder.driveStart,
              "<",
              order.deliveryEnd,
              "=",
              nextOrder.driveStart === originalEnd,
              nextOrder.driveStart < order.deliveryEnd
            );
            if (
              true ||
              nextOrder.driveStart === originalEnd ||
              nextOrder.driveStart < order.deliveryEnd
            ) {
              nextOrder.moveOrder(minutesAdded - diff, "down");
            }
          } else {
            nextOrder.driveStart = order.deliveryEnd;
          }
        }
      }
    });

    emitter.on("order-delivery-zone-changed", (e) => {
      console.log("order-delivery-zone-changed", e);
      // triggered when the order is moved to a different zone
      const order = orders.value.find((o) => o.id === e.id);

      // if the order is moved from a trip with more orders
      if (order.route_code && trips.value[order.route_code].length > 0) {
        // if is last order in the trip
        if (order.tour_code === trips.value[order.route_code].length + 1) {
          // reset drive home duration
          routeDataStore.routeData.drive_durations[`${order.route_code}_end`] = null;
        } else {
          // get the next order
          const nextOrder = trips.value[order.route_code].find(
            (o) => o.tour_code === order.tour_code + 1
          );
          if (nextOrder) {
            // reset drive duration for the next order
            nextOrder.driveDuration = null;
            nextOrder.deliveryStart = null;
            nextOrder.clearTempValues();
          }
        }
      }

      // reset drive duration and delivery start, and the drive home duration
      order.driveDuration = null;
      order.deliveryStart = null;
      order.clearTempValues();
      order.route_code = "";
      order.tour_code = "";
    });

    emitter.on("order-route-changed", (e) => {
      console.log("order-route-changed", e);
      // triggered when the order is moved to a different trip
      const order = orders.value.find((o) => o.id === e.id);
      // reset drive duration and delivery start
      order.driveDuration = null;
      order.deliveryStart = null;
      order.clearTempValues();
      // reset drive home duration for the new trip, since the order is positioned at the end of the trip
      routeDataStore.routeData.drive_durations[`${order.route_code}_end`] = null;

      // if the order is moved from a trip with more orders
      if (e.prev_route_code && trips.value[e.prev_route_code] && trips.value[e.prev_route_code].length > 0) {
        // if is last order in the trip
        if (order.tour_code === trips.value[e.prev_route_code].length + 1) {
          // reset drive home duration
          routeDataStore.routeData.drive_durations[`${e.prev_route_code}_end`] = null;
        } else {
          // get the next order
          const nextOrder = trips.value[e.prev_route_code].find(
            (o) => o.tour_code === order.tour_code + 1
          );
          if (nextOrder) {
            // reset drive duration for the next order
            nextOrder.driveDuration = null;
            nextOrder.deliveryStart = null;
            nextOrder.clearTempValues();
          }
        }
      }

      // if the order is moved to a trip
      if (e.route_code) {
        // position the order at the end of the trip
        order.tour_code = trips.value[e.route_code].length;
        // reset drive home duration for the new trip
        routeDataStore.routeData.drive_durations[`${e.trip_name}_end`] = null;

        const trip = trips.value[e.route_code];
        for (let i = 0; i < trip.length; i++) {
          trip[i].tour_code = i + 1;
          if (i > 0) {
            const prevOrder = trip[i - 1];
            const order = trip[i];
            if (prevOrder.deliveryEnd && !order.driveStart) {
              order.driveStart = prevOrder.deliveryEnd;
            } else if (prevOrder.driveEnd && !order.driveStart) {
              order.driveStart = prevOrder.driveEnd;
            }
          }
        }
      } else {
        // if the order is moved to the none trip
        // reset the tour code
        order.tour_code = "";
      }

      if (e.prev_route_code) {
        // run through all orders in the previous trip and set tour codes
        const trip = trips.value[e.prev_route_code];
        if (trip) {
          for (let i = 0; i < trip.length; i++) {
            trip[i].tour_code = i + 1;
            if (i > 0) {
              const prevOrder = trip[i - 1];
              const order = trip[i];
              if (prevOrder.deliveryEnd && !order.driveStart) {
                order.driveStart = prevOrder.deliveryEnd;
              } else if (prevOrder.driveEnd && !order.driveStart) {
                order.driveStart = prevOrder.driveEnd;
              }
            }
          }
        }
      }
    });

    emitter.on("order-tour-code-changed", (e) => {
      console.log("order-tour-code-changed", e);
      // triggered when the order in a trip has changed
      const order = orders.value.find((o) => o.id === e.id);
      // reset drive duration and delivery start
      order.driveDuration = null;
      order.deliveryStart = null;
      order.clearTempValues();
      const isMovingUp = e.prev_tour_code > e.tour_code;

      let trip = trips.value[order.route_code];

      // reset drive home duration if the order is moved to the end of the trip
      if (e.tour_code >= trip.length) {
        routeDataStore.routeData.drive_durations[`${order.route_code}_end`] = null;
      }
      // reset drive home duration if the order was positioned at the end of the trip
      if (e.prev_tour_code === trip.length) {
        routeDataStore.routeData.drive_durations[`${order.route_code}_end`] = null;
      }

      // reset drive duration for the order that was the next order
      if (e.prev_tour_code < trip.length) {
        const nextOrder = trip.find((o) => o.tour_code === e.prev_tour_code + 1);
        if (nextOrder) {
          nextOrder.driveDuration = null;
          nextOrder.deliveryStart = null;
          nextOrder.clearTempValues();
        }
      }

      // run through all orders in the trip
      console.log("a`", order.tour_code);
      for (let i = 0; i < trip.length; i++) {
        if (trip[i].id !== e.id) {
          // increment tour code for the next orders
          const isBetween = isMovingUp
            ? e.tour_code <= trip[i].tour_code && trip[i].tour_code <= e.prev_tour_code
            : e.prev_tour_code <= trip[i].tour_code && trip[i].tour_code <= e.tour_code;
          if (isBetween) {
            console.log(
              "a",
              trip[i].tour_code,
              trip[i].tour_code + (isMovingUp ? 1 : -1)
            );
            trip[i].tour_code = trip[i].tour_code + (isMovingUp ? 1 : -1);
          }
        }
      }

      // reset all tour codes for the trip in case the selected one is grater than the number of stops on the trip
      // reload trip to ensure correct order
      trip = trips.value[order.route_code];
      for (let i = 0; i < trip.length; i++) {
        console.log(trip[i].tour_code, i + 1);
        trip[i].tour_code = i + 1;
        if (i > 0) {
          // autofill driveStart when possible
          const prevOrder = trip[i - 1];
          const order = trip[i];
          if (prevOrder.deliveryEnd && !order.driveStart) {
            order.driveStart = prevOrder.deliveryEnd;
          } else if (prevOrder.driveEnd && !order.driveStart) {
            order.driveStart = prevOrder.driveEnd;
          }
        }
      }
    });

    const getNextOrder = (orderId, tripName) => {
      const tripOrders = trips.value[tripName];
      for (let i = 0; i < tripOrders.length; i++) {
        if (tripOrders[i].id === orderId) {
          if (i < tripOrders.length - 1) {
            return tripOrders[i + 1];
          } else {
            return null;
          }
        }
      }
      return null;
    };

    const getNextTripName = (tripName) => {
      if (tripName === "none" || tripName === null) {
        return null;
      }
      const index = tripNames.indexOf(tripName);
      if (index < tripNames.length - 1) {
        return tripNames[index + 1];
      } else {
        return null;
      }
    };

    const getFirstOrderInTrip = (tripName) => {
      const tripOrders = trips.value[tripName];
      if (tripOrders.length > 0) {
        return tripOrders[0];
      } else {
        const nextTripName = getNextTripName(tripName);
        if (nextTripName) {
          return getFirstOrderInTrip(nextTripName);
        } else {
          return null;
        }
      }
    };

    const getPrevOrder = (orderId, tripName) => {
      const tripOrders = trips.value[tripName];
      for (let i = 0; i < tripOrders.length; i++) {
        if (tripOrders[i].id === orderId) {
          if (i > 0) {
            return tripOrders[i - 1];
          } else {
            return null;
          }
        }
      }
      return null;
    };

    const getPrevTripName = (tripName) => {
      const index = tripNames.indexOf(tripName);
      if (index > 0) {
        return tripNames[index - 1];
      } else {
        return null;
      }
    };

    const getLastOrderInTrip = (tripName) => {
      if (tripName === "none" || tripName === null) {
        return null;
      }
      const tripOrders = trips.value[tripName];
      if (tripOrders.length > 0) {
        return tripOrders[tripOrders.length - 1];
      } else {
        const prevTripName = getPrevTripName(tripName);
        if (prevTripName) {
          return getLastOrderInTrip(prevTripName);
        } else {
          return null;
        }
      }
    };

    const saveRouteData = () => {
      loading.value = true;
      axios
        .put(
          `/order_management/orders/save_route_data?type=deliveries&date=${props.date_string}&delivery_zone=${props.delivery_zone_id}`,
          getSaveRequestData()
        )
        .then((response) => {
          console.log("saveRouteData", response.data);
        })
        .finally(() => {
          loading.value = false;
          data_logs.value.loadLogs();
          chatChannel.send({ type: "data saved", sender: sessionId });
          // remove orders with other delivery zone
          orders.value = orders.value.filter(
            (order) => order.delivery_zone_id === props.delivery_zone_id
          );

          lastSaveRequestData.value = getSaveRequestData();
        });
    };
    const getSaveRequestData = (includeOtherDeliveryZones = true) => {
      const routeData = JSON.parse(JSON.stringify(routeDataStore.routeData));
      const orderIdsWithSameDeliveryZone = orders.value
        .filter((order) => order.delivery_zone_id === props.delivery_zone_id)
        .map((order) => order.id);
      const activeTripNames = tripNames.filter((name) => trips.value[name].length > 0);
      let keys = Object.keys(routeData.drive_durations);
      for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        if (!`${key}`.includes("_")) {
          if (!`${key}`.includes("-")) {
            key = parseInt(key);
          }
          if (!orderIdsWithSameDeliveryZone.includes(key)) {
            delete routeData.drive_durations[key];
          }
        } else {
          const tripname = key.replace(/_.*$/, "");
          if (!activeTripNames.includes(tripname)) {
            delete routeData.drive_durations[key];
          }
        }
      }
      keys = Object.keys(routeData.service_durations);
      for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        if (!`${key}`.includes("_")) {
          if (!`${key}`.includes("-")) {
            key = parseInt(key);
          }
          if (!orderIdsWithSameDeliveryZone.includes(key)) {
            delete routeData.service_durations[key];
          }
        } else {
          const tripname = key.replace(/_.*$/, "");
          if (!activeTripNames.includes(tripname)) {
            delete routeData.service_durations[key];
          }
        }
      }
      routeData.manual_stops = onlyManualStops.value.map((order) => {
        return {
          id: order.id,
          delivery_zone_id: order.delivery_zone_id,
          route_code: order.route_code,
          tour_code: order.tour_code,
          manualStopAddress: order.manualStopAddress,
          manualStopDescription: order.manualStopDescription,
          expected_delivery_time: order.expected_delivery_time,
          vehicle_id: order.vehicle_id,
          driver_id: order.driver_id,
        };
      });
      const res = {
        route_data: routeData,
        ready_for_print: isReadyForPrint.value,
        orders: ordersWithoutManualStops.value
          .filter(
            (order) =>
              includeOtherDeliveryZones ||
              order.delivery_zone_id === props.delivery_zone_id
          )
          .map((order) => {
            if (
              order.route_code === "none" ||
              order.delivery_zone_id !== props.delivery_zone_id
            ) {
              return {
                id: order.id,
                delivery_zone_id: order.delivery_zone_id,
                route_code: null,
                tour_code: null,
                expected_delivery_time: null,
                vehicle_id: null,
                driver_id: null,
              };
            } else {
              return {
                id: order.id,
                delivery_zone_id: order.delivery_zone_id,
                route_code: order.route_code,
                tour_code: order.tour_code,
                expected_delivery_time: order.deliveryStart,
                vehicle_id: order.vehicle_id,
                driver_id: order.driver_id,
              };
            }
          })
          .sort((a, b) => a.id - b.id),
      };
      // console.log('getSaveRequestData', res);
      console.log("routeData", routeData);
      return res;
    };

  const lastSaveRequestData = ref(null);
    const t = setInterval(() => {
      if (routeDataStore.routeData) {
        lastSaveRequestData.value = getSaveRequestData();
        clearInterval(t);
      }
    }, 100);
    const isDirty = computed(() => {
      if (!lastSaveRequestData.value) {
        return false;
      }
      console.log(
        "isDirty",
        JSON.parse(JSON.stringify(lastSaveRequestData.value)),
        getSaveRequestData()
      );
      return (
        JSON.stringify(lastSaveRequestData.value) !== JSON.stringify(getSaveRequestData())
      );

      if (!data_logs.value || data_logs.value.logs.length === 0) {
        return null;
      }
      const dataLog = JSON.parse(JSON.stringify(data_logs.value.logs[0]));
      dataLog.data.orders = dataLog.data.orders.filter(
        (o) => o.delivery_zone_id === props.delivery_zone_id
      );
      console.log("isDirty", dataLog.data, getSaveRequestData(false));
      // console.log('b', JSON.stringify(dataLog.data), JSON.stringify(getSaveRequestData(false)));
      return JSON.stringify(dataLog.data) !== JSON.stringify(getSaveRequestData(false));
    });

    window.onbeforeunload = function () {
      if (isDirty.value && !isLocked.value) {
        return "Du har ugemte ændringer. Er du sikker på at du vil forlade siden?";
      }
    };

    const vehicleChange = (tripName, vehicleId) => {
      let hasAnyTripSelected =
        tripNames
          .map((name) => {
            if (trips.value[name].length > 0)
              return (
                name !== tripName &&
                trips.value[name].length > 0 &&
                trips.value[name][0].vehicle_id !== null
              );
          })
          .filter((e) => e).length > 0;
      if (!hasAnyTripSelected) {
        for (let i = 0; i < orders.value.length; i++) {
          orders.value[i].vehicle_id = vehicleId;
        }
      } else {
        const trip = trips.value[tripName];
        for (let i = 0; i < trip.length; i++) {
          trip[i].vehicle_id = vehicleId;
        }
      }
    };
    const driverChange = (tripName, driverId) => {
      let hasAnyTripSelected =
        tripNames
          .map((name) => {
            return (
              name !== tripName &&
              trips.value[name].length > 0 &&
              trips.value[name][0].driver_id !== null
            );
          })
          .filter((e) => e).length > 0;
      if (!hasAnyTripSelected) {
        for (let i = 0; i < orders.value.length; i++) {
          orders.value[i].driver_id = driverId;
        }
      } else {
        const trip = trips.value[tripName];
        for (let i = 0; i < trip.length; i++) {
          trip[i].driver_id = driverId;
        }
      }
    };

    const applyLog = (log) => {
      for (let i = 0; i < orders.value.length; i++) {
        const orderId = orders.value[i].id;
        const orderLog = log.data.orders.find((o) => o.id === orderId);
        if (orderLog) {
          console.log(
            "A",
            JSON.parse(JSON.stringify(orders.value[i])),
            orderLog.expected_delivery_time,
            moment(orderLog.expected_delivery_time, "HH:mm").format("YYYY-MM-DDTHH:mmZ")
          );
          orders.value[i].route_code = orderLog.route_code;
          orders.value[i].tour_code = orderLog.tour_code;
          orders.value[i].expected_delivery_time = orderLog.expected_delivery_time
            ? moment(orderLog.expected_delivery_time, "HH:mm").format("YYYY-MM-DDTHH:mmZ")
            : null; // 2000-01-01T08:30:00.000Z
          orders.value[i].vehicle_id = orderLog.vehicle_id;
          orders.value[i].driver_id = orderLog.driver_id;
          console.log("B", JSON.parse(JSON.stringify(orders.value[i])));
        } else {
          console.log("C");
          orders.value[i].route_code = null;
          orders.value[i].tour_code = null;
          orders.value[i].expected_delivery_time = null;
          orders.value[i].vehicle_id = null;
          orders.value[i].driver_id = null;
        }
      }
      routeDataStore.setRouteData(log.data.route_data);
    };

    const tripTotalQuantity = (orders) => {
      return orders.reduce((acc, order) => {
        return acc + order.total_quantity;
      }, 0);
    };

    const tripBoxCount = (orders) => {
      return orders.reduce((acc, order) => {
        return acc + order.box_count;
      }, 0);
    };

    const safeTransportTimeLimits = computed(() => {
      return {
        tidPaaBilRodAdvarselKold: props.settings['tid_pa_bil_rod_advarsel_kold'],
        tidPaaBilGulAdvarselKold: props.settings['tid_pa_bil_gul_advarsel_kold'],
        tidPaaBilRodAdvarselVarm: props.settings['tid_pa_bil_rod_advarsel_varm'],
        tidPaaBilGulAdvarselVarm: props.settings['tid_pa_bil_gul_advarsel_varm'],
      }
    });



    const editingStop = reactive({
      id: null,
      description: '',
      address: ''
    })

    let modalInstance = null

    const openModal = (id = null) => {
      console.log('openModal', id);
      if (id) {
        const stop = orders.value.find(o => o.id === id)
        if (stop) {
          editingStop.id = stop.id
          editingStop.description = stop.manualStopDescription
          editingStop.address = stop.manualStopAddress
        }
      } else {
        editingStop.id = null
        editingStop.description = ''
        editingStop.address = ''
      }
      console.log('openModal')
      if (modalInstance) {
        modalInstance.show()
      }
    }

    const closeModal = () => {
      if (modalInstance) {
        modalInstance.hide()
      }
    }

    const saveStop = () => {
      console.log('Stop added')
      
      if (editingStop.id) {
        const stop = orders.value.find(stop => stop.id === editingStop.id)
        if (stop) {
          stop.manualStopDescription = editingStop.description
          stop.manualStopAddress = editingStop.address
        }
      } else {
        orders.value.push(Order.createReactive({
          // id: 'manual-stop-' + onlyManualStops.value.length,
          id: random(8) + '-' + random(8), // should contain a dash, as this is used to check if the order is a manual stop
          delivery_zone_id: props.delivery_zone_id,
          route_code: null,
          tour_code: null,
          isManualStop: true,
          manualStopAddress: editingStop.address,
          manualStopDescription: editingStop.description,
        }));

        // ensure that all variables in route data are reactive
        const newRouteData = setAllVariables(orders.value, routeDataStore.routeData);
      routeDataStore.setRouteData(newRouteData);
      }
      closeModal()
    }

    const deleteStop = () => {
      console.log('Stop deleted');
      const stop = orders.value.find(o => o.id === editingStop.id);
      if (stop) {
        const index = orders.value.indexOf(stop)
        if (index > -1) {
          orders.value.splice(index, 1)
        }
      }
      closeModal()
    }

    const showDeleteStopButton = computed(() => {
      if (editingStop.id === null) {
        return false
      } else {
        const stop = orders.value.find(o => o.id === editingStop.id);
        return stop && !stop.route_code && !stop.tour_code
      }
    });

    const saveStopModal = ref(null)

    onMounted(() => {
      if (saveStopModal.value) {
        // @ts-ignore
        modalInstance = new bootstrap.Modal(saveStopModal.value);
      }
    })

    return {
      loading,
      hasCompletedFirstLoad,
      isLocked,
      isLockedByUserName,
      showLockedTooltip,
      forceTakeLock,
      date,
      warnings,
      googleMapsDefaultUrl,
      tripNames,
      trips,
      saveRouteData,
      isDirty,
      vehicleChange,
      driverChange,
      applyLog,
      data_logs,
      tripTotalQuantity,
      tripBoxCount,
      safeTransportTimeLimits,
      saveStop,
      openModal,
      closeModal,
      deleteStop,
      showDeleteStopButton,
      saveStopModal,
      editingStop,
      orders,
      bookingGuideModal,
      openBookingGuideModal,
      isReadyForPrint,
    };
  },
};
</script>

<style lang="scss" scoped>
.delivery-page-component {
  white-space: nowrap;
  position: relative;

  .table-responsive {
    max-height: calc(100vh - 315px);
  }

  .action-container {
    position: absolute;
    bottom: 100%;
    right: 0;
    background-color: white;
    z-index: 1000;
    padding: 10px 0;
    display: flex;
  }

  table {
    thead {
      position: sticky;
      top: 0;
      background-color: white;
      box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
      z-index: 1;
    }

    th,
    td {
      vertical-align: middle;
    }

    .trip-block-none {
      position: sticky;
      top: 54px;
      background-color: #ffe083 !important;
      box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
    }
  }
}

.interval-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 5px;
  border: 1px solid white;
  margin-left: -6px;
}

.interval-count {
  width: 18px;
  display: inline-block;
}

.locked-tooltip {
  position: absolute;
  top: 100%;
  right: 10px;
  background-color: white;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  z-index: 100;
  max-height: 50vh;
  width: 300px;
  white-space: normal;
}
</style>
<style lang="scss">
.delivery-page-component {
  table {

    th,
    td {
      border: none !important;

      vertical-align: middle;

      &.pull-down {
        padding-top: 0.5rem;
        padding-bottom: 0.1rem;
      }

      &.pull-up {
        padding-top: 0.1rem;
        padding-bottom: 0.5rem;
      }

      &.cell-highlight {
        input {
          border: 2px solid #444;
          font-weight: bold;
        }
      }
    }
  }

  &.locked {
    .is-lockable {
      pointer-events: none;
      opacity: 0.7;

      input,
      select {
        background-color: #eee;
      }
    }
  }

  .ready-for-print-container {
    display: flex;
    align-items: center;

    label {
      display: flex;
      align-items: center;
      margin-bottom: 0;
    }
  }
}
</style>
